import { useCallback } from 'react';
import { useNavigationManager } from '@provider/navigation';
import { useNotifier } from '@provider/notifier';
import { safeLocalStorage } from '@utils/common';
import { firstInteractionConfirmConfig } from '../modals/first-interaction.js';
import { courseRevisionsStore } from '../store.js';
import { useGetAllRevisionsQuery } from '../api/query.get-all.js';

const localStorageKey = "pm2:course-revisions/has-created-revision";

/**
 * prepares a callback that can be used to starting editing mode of a new
 * revision for the current course.
 */
function useStartRevision(courseId, navigateTo) {
  const notifier = useNotifier();
  const navigation = useNavigationManager();
  const revisions = useGetAllRevisionsQuery(courseId);
  return useCallback(async baseRevision => {
    const hasInteractedBefore = safeLocalStorage.getItem(localStorageKey) === "true" || !!revisions.data?.length;
    if (!hasInteractedBefore) {
      if ((await notifier.confirm(firstInteractionConfirmConfig)) !== "ok") {
        return;
      }
      safeLocalStorage.setItem(localStorageKey, "true");
    }
    const resolvedNavigateTo = typeof navigateTo === "string" ? navigateTo : navigateTo?.();
    const startRevision = () => {
      courseRevisionsStore.dispatch(courseRevisionsStore.actions.toggleEditMode({
        state: true,
        editedRevision: baseRevision
      }));
    };
    if (resolvedNavigateTo) {
      navigation.push(resolvedNavigateTo);

      // Revisions can only be applied within the context of course content,
      // as such we wait until navigation has been completed before toggling
      // edit mode.
      const unsubscribe = navigation.subscribe(() => {
        startRevision();
        unsubscribe();
      });
    } else {
      startRevision();
    }
  }, [navigateTo, navigation, notifier, revisions.data?.length]);
}

export { useStartRevision };
//# sourceMappingURL=start.js.map
